import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateExternalTagRequest,
  CreateExternalTemplateRequest,
  ExternalTagResponse,
  ExternalTemplateResponse,
  UpdateExternalTagRequest,
  UpdateExternalTemplateRequest,
} from '@models/notifications/external-templates/external-templates.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { ExternalTemplatesService } from './external-templates.service';

type ExternalTemplatesState = {
  templates: ExternalTemplateResponse[];
  isLoading: boolean;
  templateCreateLoading: boolean;

  selectedTemplate: ExternalTemplateResponse | null;
  selectedTemplateLoading: boolean;

  tagCreateLoading: boolean;
  tags: ExternalTagResponse[];
  tagsLoading: boolean;

  selectedTag: ExternalTagResponse | null;
  selectedTagLoading: boolean;
};

const initialState: ExternalTemplatesState = {
  templates: [],
  isLoading: false,
  templateCreateLoading: false,

  selectedTemplate: null,
  selectedTemplateLoading: false,

  tagCreateLoading: false,
  tags: [],
  tagsLoading: false,

  selectedTag: null,
  selectedTagLoading: false,
};

const externalTemplatesDictionary: StateDictionary<ExternalTemplateResponse> = {
  entityList: 'templates',
  listLoading: 'isLoading',
  selectedEntity: 'selectedTemplate',
  selectedEntityLoading: 'selectedTemplateLoading',
  entityId: 'id',
  createLoading: 'templateCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const ExternalTemplatesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('external-templates-store'),

  withComputed(state => ({
    selectedTemplateId: computed(() => state.selectedTemplate()?.id || null),
    selectedTagId: computed(() => state.selectedTag()?.id || null),
  })),

  withMethods((store, externalTemplatesService = inject(ExternalTemplatesService), router = inject(Router)) => {
    createStateResetHandler<ExternalTemplatesState>('/notifications/external-templates')(store, initialState);
    createCustomStateSetOnUrlHandler<ExternalTemplatesState>('/notifications/external-templates', {
      selectedTemplate: null,
      selectedTag: null,
    })(store);
    createCustomStateSetOnRegexHandler<ExternalTemplatesState>(new RegExp(`^/notifications/external-templates/${guidRegex}/tags$`))(store, {
      selectedTag: null,
    });

    const repositoryMethods = {
      getAll: getAll<ExternalTemplateResponse>({
        store,
        service: externalTemplatesService,
        dictionary: externalTemplatesDictionary,
      }),
      getOne: getOne<ExternalTemplateResponse>({
        store,
        service: externalTemplatesService,
        dictionary: externalTemplatesDictionary,
      }),
      add: addEntity<ExternalTemplateResponse, CreateExternalTemplateRequest>({
        store,
        service: externalTemplatesService,
        dictionary: externalTemplatesDictionary,
        onSuccessCallback: newTemplate => {
          router.navigate(['notifications', 'external-templates', newTemplate.id, 'details']);
        },
      }),
      update: updateEntity<ExternalTemplateResponse, UpdateExternalTemplateRequest>({
        store,
        service: externalTemplatesService,
        dictionary: externalTemplatesDictionary,
      }),
      delete: deleteEntity<ExternalTemplateResponse>({
        store,
        service: externalTemplatesService,
        dictionary: externalTemplatesDictionary,
        onSuccessCallback: () => {
          router.navigate(['notifications', 'external-templates']);
          repositoryMethods.getAll();
        },
      }),
    };

    // Tags methods
    const tagsMethods = {
      getAllTags: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { tags: [], tagsLoading: true })),
          switchMap(templateId =>
            externalTemplatesService.getAllTags(templateId).pipe(
              tap(tags => {
                patchState(store, {
                  tagsLoading: false,
                  tags: sortAlphabetically([...tags], (a, b) => a.name.localeCompare(b.name)),
                });
              }),
              catchError(() => {
                patchState(store, { tagsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      addTag: rxMethod<{ templateId: string; tag: CreateExternalTagRequest }>(
        pipe(
          tap(() => patchState(store, { tagsLoading: true, tagCreateLoading: true })),
          switchMap(({ templateId, tag }) =>
            externalTemplatesService.addTag(templateId, tag).pipe(
              tap(tag => {
                patchState(store, state => ({
                  tagCreateLoading: false,
                  tagsLoading: false,
                  selectedTag: tag,
                  tags: sortAlphabetically([...state.tags, tag], (a, b) => a.name.localeCompare(b.name)),
                }));
                router.navigate(['notifications', 'external-templates', templateId, 'tags', tag.id, 'details']);
              }),
              catchError(() => {
                patchState(store, { tagCreateLoading: false, tagsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      updateTag: rxMethod<{ templateId: string; tagId: string; tag: UpdateExternalTagRequest }>(
        pipe(
          tap(() => patchState(store, { selectedTagLoading: true, tagsLoading: true })),
          switchMap(({ templateId, tagId, tag }) =>
            externalTemplatesService.updateTag(templateId, tagId, tag).pipe(
              tap(tag => {
                patchState(store, state => ({
                  tagsLoading: false,
                  selectedTagLoading: false,
                  selectedTag: tag,
                  tags: sortAlphabetically(
                    findAndReplace(state.tags, tag, item => item.id === tag.id),
                    (a, b) => a.name.localeCompare(b.name),
                  ),
                }));
              }),
              catchError(() => {
                patchState(store, state => ({
                  selectedTag: { ...state.selectedTag! },
                  tagsLoading: false,
                  selectedTagLoading: false,
                }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      deleteTag: rxMethod<{ templateId: string; tagId: string }>(
        pipe(
          tap(() => patchState(store, { selectedTagLoading: true })),
          switchMap(({ templateId, tagId }) =>
            externalTemplatesService.deleteTag(templateId, tagId).pipe(
              tap(() => {
                patchState(store, {
                  selectedTagLoading: false,
                  selectedTag: null,
                });
                router.navigate(['notifications', 'external-templates', templateId, 'tags']);
                tagsMethods.getAllTags(templateId);
              }),
              catchError(() => {
                patchState(store, { selectedTagLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      selectTag: rxMethod<{ templateId: string; tagId: string }>(
        pipe(
          tap(() => {
            patchState(store, { selectedTagLoading: true });
          }),
          switchMap(({ templateId, tagId }) =>
            externalTemplatesService.getTag(templateId, tagId).pipe(
              tap(tag => patchState(store, { selectedTagLoading: false, selectedTag: tag })),
              catchError(() => {
                patchState(store, { selectedTagLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    // Navigation methods
    const navigationActions = {
      goToTemplateActions: async (id: string): Promise<boolean> => {
        const success = await router.navigate(['notifications', 'external-templates', id]);
        if (success) {
          repositoryMethods.getOne(id);
        }
        return success;
      },
      goToTemplatesListPage: (): void => {
        patchState(store, { selectedTemplate: null });
        router.navigate(['notifications', 'external-templates']);
      },
      goToTagActions: async (tagId: string): Promise<boolean> => {
        if (store.selectedTemplateId() !== null) {
          const success = await router.navigate(['notifications', 'external-templates', store.selectedTemplateId(), 'tags', tagId]);
          if (success) {
            tagsMethods.selectTag({ templateId: store.selectedTemplateId()!, tagId });
          }
          return success;
        }
        return false;
      },
      goToTagsListPage: (): void => {
        if (store.selectedTemplateId() !== null) {
          patchState(store, { selectedTag: null });
          router.navigate(['notifications', 'external-templates', store.selectedTemplateId(), 'tags']);
        }
      },
    };

    return {
      ...repositoryMethods,
      ...tagsMethods,
      ...navigationActions,
    };
  }),
);

export interface ExternalTemplatesStoreType {
  // state signals
  readonly templates: Signal<ExternalTemplateResponse[]>;
  readonly isLoading: Signal<boolean>;
  readonly templateCreateLoading: Signal<boolean>;
  readonly selectedTemplate: Signal<ExternalTemplateResponse | null>;
  readonly selectedTemplateLoading: Signal<boolean>;
  readonly tagCreateLoading: Signal<boolean>;
  readonly tags: Signal<ExternalTagResponse[]>;
  readonly tagsLoading: Signal<boolean>;
  readonly selectedTag: Signal<ExternalTagResponse | null>;
  readonly selectedTagLoading: Signal<boolean>;

  // computed signals
  readonly selectedTemplateId: Signal<string | null>;
  readonly selectedTagId: Signal<string | null>;

  // template methods
  getAll: () => void;
  getOne: (id: string) => void;
  add: (request: CreateExternalTemplateRequest) => void;
  update: (input: { id: string; dto: UpdateExternalTemplateRequest }) => void;
  delete: (id: string) => void;

  // tag methods
  getAllTags: (templateId: string) => void;
  addTag: (input: { templateId: string; tag: CreateExternalTagRequest }) => void;
  updateTag: (input: { templateId: string; tagId: string; tag: UpdateExternalTagRequest }) => void;
  deleteTag: (input: { templateId: string; tagId: string }) => void;
  selectTag: (input: { templateId: string; tagId: string }) => void;

  // navigation methods
  goToTemplateActions: (id: string) => Promise<boolean>;
  goToTemplatesListPage: () => void;
  goToTagActions: (tagId: string) => Promise<boolean>;
  goToTagsListPage: () => void;
}
